import { FBO, Hangar, Ramp, SOPOverrides } from "../types";

export type ResolvedSOPs = {
  sop_horizontal_spacing: number;
  sop_vertical_spacing: number;
  sop_overlap: boolean;
  sop_horizontal_spacing_tight: number;
  sop_vertical_spacing_tight: number;
  sop_overlap_tight: boolean;
  sop_horizontal_spacing_loose: number;
  sop_vertical_spacing_loose: number;
  sop_overlap_loose: boolean;
  sop_ramp_horizontal_spacing: number;
  sop_orientation_nose_in_threshold: number;
  sop_orientation_tail_in_threshold: number;
};

/**
 * Resolves effective SOPs for a location by merging FBO defaults with location overrides.
 * Location overrides take precedence; missing keys fall back to FBO defaults.
 *
 * @param fbo - The FBO containing default SOP values
 * @param location - The hangar or ramp with optional overrides (null/undefined = use all FBO defaults)
 * @returns The effective SOP values to use for this location
 *
 * @example
 * // Hangar with some overrides
 * const sops = getSOPsForLocation(activeFBO, hangar);
 * const spacing = sops.sop_horizontal_spacing;
 *
 * @example
 * // Ramp with no overrides (uses all FBO defaults)
 * const sops = getSOPsForLocation(activeFBO, ramp);
 */
export function getSOPsForLocation(
  fbo: FBO,
  location: Hangar | Ramp | null | undefined
): ResolvedSOPs {
  const overrides = location?.sop_overrides ?? {};

  return {
    sop_horizontal_spacing:
      overrides.sop_horizontal_spacing ?? fbo.sop_horizontal_spacing,
    sop_vertical_spacing:
      overrides.sop_vertical_spacing ?? fbo.sop_vertical_spacing,
    sop_overlap: overrides.sop_overlap ?? fbo.sop_overlap,
    sop_horizontal_spacing_tight:
      overrides.sop_horizontal_spacing_tight ?? fbo.sop_horizontal_spacing_tight,
    sop_vertical_spacing_tight:
      overrides.sop_vertical_spacing_tight ?? fbo.sop_vertical_spacing_tight,
    sop_overlap_tight: overrides.sop_overlap_tight ?? fbo.sop_overlap_tight,
    sop_horizontal_spacing_loose:
      overrides.sop_horizontal_spacing_loose ?? fbo.sop_horizontal_spacing_loose,
    sop_vertical_spacing_loose:
      overrides.sop_vertical_spacing_loose ?? fbo.sop_vertical_spacing_loose,
    sop_overlap_loose: overrides.sop_overlap_loose ?? fbo.sop_overlap_loose,
    sop_ramp_horizontal_spacing:
      overrides.sop_ramp_horizontal_spacing ?? fbo.sop_ramp_horizontal_spacing,
    sop_orientation_nose_in_threshold:
      overrides.sop_orientation_nose_in_threshold ??
      fbo.sop_orientation_nose_in_threshold,
    sop_orientation_tail_in_threshold:
      overrides.sop_orientation_tail_in_threshold ??
      fbo.sop_orientation_tail_in_threshold,
  };
}

/**
 * Returns true if location has any SOP overrides defined.
 */
export function hasSOPOverrides(
  location: Hangar | Ramp | null | undefined
): boolean {
  if (!location?.sop_overrides) return false;
  return Object.keys(location.sop_overrides).length > 0;
}

/**
 * Counts how many SOP fields are overridden for a location.
 */
export function countSOPOverrides(
  location: Hangar | Ramp | null | undefined
): number {
  if (!location?.sop_overrides) return 0;
  return Object.keys(location.sop_overrides).filter(
    (key) =>
      location.sop_overrides![key as keyof SOPOverrides] !== undefined
  ).length;
}

/**
 * List of all SOP field keys for iteration.
 */
export const SOP_FIELDS: (keyof SOPOverrides)[] = [
  "sop_horizontal_spacing",
  "sop_vertical_spacing",
  "sop_overlap",
  "sop_horizontal_spacing_tight",
  "sop_vertical_spacing_tight",
  "sop_overlap_tight",
  "sop_horizontal_spacing_loose",
  "sop_vertical_spacing_loose",
  "sop_overlap_loose",
  "sop_ramp_horizontal_spacing",
  "sop_orientation_nose_in_threshold",
  "sop_orientation_tail_in_threshold",
];

/**
 * Human-readable labels for SOP fields.
 */
export const SOP_FIELD_LABELS: Record<keyof SOPOverrides, string> = {
  sop_horizontal_spacing: "Horizontal Spacing (Normal)",
  sop_vertical_spacing: "Vertical Spacing (Normal)",
  sop_overlap: "Allow Overlap (Normal)",
  sop_horizontal_spacing_tight: "Horizontal Spacing (Tight)",
  sop_vertical_spacing_tight: "Vertical Spacing (Tight)",
  sop_overlap_tight: "Allow Overlap (Tight)",
  sop_horizontal_spacing_loose: "Horizontal Spacing (Loose)",
  sop_vertical_spacing_loose: "Vertical Spacing (Loose)",
  sop_overlap_loose: "Allow Overlap (Loose)",
  sop_ramp_horizontal_spacing: "Ramp Horizontal Spacing",
  sop_orientation_nose_in_threshold: "Nose-In Threshold",
  sop_orientation_tail_in_threshold: "Tail-In Threshold",
};

/**
 * Groups SOP fields by category for UI organization.
 */
export const SOP_FIELD_GROUPS = {
  hangarNormal: [
    "sop_horizontal_spacing",
    "sop_vertical_spacing",
    "sop_overlap",
  ] as (keyof SOPOverrides)[],
  hangarTight: [
    "sop_horizontal_spacing_tight",
    "sop_vertical_spacing_tight",
    "sop_overlap_tight",
  ] as (keyof SOPOverrides)[],
  hangarLoose: [
    "sop_horizontal_spacing_loose",
    "sop_vertical_spacing_loose",
    "sop_overlap_loose",
  ] as (keyof SOPOverrides)[],
  orientation: [
    "sop_orientation_nose_in_threshold",
    "sop_orientation_tail_in_threshold",
  ] as (keyof SOPOverrides)[],
  ramp: ["sop_ramp_horizontal_spacing"] as (keyof SOPOverrides)[],
};

/**
 * Returns fields relevant for a location type.
 * Hangars use all fields; ramps use only ramp-specific fields.
 */
export function getRelevantSOPFields(
  locationType: "hangar" | "ramp"
): (keyof SOPOverrides)[] {
  if (locationType === "ramp") {
    return ["sop_ramp_horizontal_spacing"];
  }
  return SOP_FIELDS;
}
